import React, { useCallback, useMemo, memo } from "react";
import { GiFrayedArrow } from "react-icons/gi";
import logo from "../../Assets/images/logoTechmenteeColour.png";
import {
  FaArrowUp,
  FaFacebookF,
  FaLinkedinIn,
  FaTwitter,
  FaYoutube,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useNewsletter from "../Common/useNewsletter";
import useScrollToTop from "../Common/useScrollToTop";
import usePrograms from "../Common/usePrograms";

// Social media links configuration
const SOCIAL_LINKS = [
  {
    icon: FaTwitter,
    color: "bg-[#1DA1F2]",
    url: "#",
    ariaLabel: "Follow us on Twitter",
  },
  {
    icon: FaFacebookF,
    color: "bg-[#4267B2]",
    url: "#",
    ariaLabel: "Follow us on Facebook",
  },
  {
    icon: FaLinkedinIn,
    color: "bg-[#0077B5]",
    url: "#",
    ariaLabel: "Follow us on LinkedIn",
  },
  {
    icon: FaYoutube,
    color: "bg-[#FF0000]",
    url: "#",
    ariaLabel: "Subscribe to our YouTube channel",
  },
];

const SocialIcon = memo(({ icon: Icon, color, url, ariaLabel }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={ariaLabel}
    className={`w-10 h-10 ${color} flex items-center justify-center rounded-lg transition-all duration-300 hover:scale-110 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50`}
  >
    <Icon size={20} className="text-white" />
  </a>
));

const NavigationLink = memo(({ to, children, className = "" }) => (
  <Link
    to={to}
    className={`hover:text-primaryOrangeColor transition-colors duration-200 focus:outline-none focus:text-primaryOrangeColor ${className}`}
  >
    {children}
  </Link>
));

const Footer = () => {
  const navigate = useNavigate();
  const { isVisible, scrollToTop } = useScrollToTop();
  const { programs, loading, error } = usePrograms();
  const {
    email,
    setEmail,
    isSubmitting,
    submitStatus,
    handleSubscribe,
    handleKeyPress,
    isValidEmail,
  } = useNewsletter();

  const navigateToProgram = useCallback(
    (post) => {
      const titleWithDashes = post.title.replace(/(?!\s&|&\s)\s+/g, "-");
      navigate(`/knowledge/program/${encodeURIComponent(titleWithDashes)}`, {
        state: { post },
      });
    },
    [navigate],
  );

  const quickLinks = useMemo(
    () => [
      { to: "/about/about-techmentee", label: "About" },
      { to: "/contact-us/partner-with-us", label: "Partner With Us" },
      { to: "/contact-us/career", label: "Career" },
      { to: "/help-faqs", label: "Help & FAQ" },
      { to: "/privacy-policy", label: "Privacy Policy" },
    ],
    [],
  );

  const services = useMemo(
    () => [
      {
        to: "/serviceDetails/6a27f28d33a3300f62453f64",
        label: "Application Development",
      },
      {
        to: "/serviceDetails/6a281421a698efb2ced77190",
        label: "Microsoft Services",
      },
      {
        to: "/serviceDetails/6a281492a698efb2ced77194",
        label: "Business Analysis Service",
      },
      {
        to: "/serviceDetails/6a29524cf3b8f563c5c21fed",
        label: "Business Intelligence and Data Analytics",
      },
      { to: "/serviceDetails/6a2955c2f3b8f563c5c2214e", label: "QA & Testing" },
      { to: "/serviceDetails/6a315c3d6b2a2200d6360cd8", label: "IT Staffing" },
      // { to: "/service/immigrationSupport", label: "Immigration Support" },
    ],
    [],
  );

  const getInputBorderColor = () => {
    if (submitStatus === "success") return "border-green-500";
    if (submitStatus === "error") return "border-red-500";
    if (email && !isValidEmail) return "border-red-400";
    if (email && isValidEmail) return "border-green-400";
    return "border-gray-300";
  };

  const getButtonContent = () => {
    if (isSubmitting) return <FaSpinner className="animate-spin" size={16} />;
    if (submitStatus === "success") return <FaCheckCircle size={16} />;
    if (submitStatus === "error") return <FaExclamationTriangle size={16} />;
    return "Subscribe";
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      {/* Main Footer */}
      <footer
        className="bg-gradient-to-br from-[#131F22] to-[#0f1a1d] lg:px-20 md:px-10 px-5 py-16"
        role="contentinfo"
      >
        {/* Newsletter Section */}
        <section
          className="text-center mb-16"
          aria-labelledby="newsletter-heading"
        >
          <h2
            id="newsletter-heading"
            className="text-white tracking-wider text-4xl font-bold mb-4"
          >
            Join Our Newsletter
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Stay updated with the latest trends, insights, and opportunities in
            tech
          </p>

          <div className="flex justify-center items-center gap-8 max-w-4xl mx-auto">
            <div className="hidden md:block">
              <GiFrayedArrow
                size={40}
                className="text-primaryOrangeColor animate-pulse"
              />
            </div>

            <div className="flex gap-3 lg:w-[40%] w-full max-w-md">
              <div className="relative flex-1">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter your email address..."
                  className={`px-4 py-3 w-full rounded-lg border-2 ${getInputBorderColor()} focus:outline-none focus:ring-2 focus:ring-primaryOrangeColor focus:border-transparent transition-all duration-200 text-gray-800`}
                  disabled={isSubmitting}
                  aria-label="Email address for newsletter subscription"
                  autoComplete="email"
                />
                {email && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {isValidEmail ? (
                      <FaCheckCircle className="text-green-500" size={16} />
                    ) : (
                      <FaExclamationTriangle
                        className="text-red-500"
                        size={16}
                      />
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={handleSubscribe}
                disabled={isSubmitting || !email.trim()}
                className={`bg-primaryOrangeColor hover:bg-primaryDarkColor disabled:bg-gray-500 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center min-w-[120px] focus:outline-none focus:ring-2 focus:ring-primaryOrangeColor focus:ring-offset-2 focus:ring-offset-[#131F22]`}
                aria-label="Subscribe to newsletter"
              >
                {getButtonContent()}
              </button>
            </div>

            <div className="hidden md:block">
              <GiFrayedArrow
                size={40}
                className="transform -scale-x-100 text-primaryOrangeColor animate-pulse"
              />
            </div>
          </div>
        </section>

        {/* Main Footer Content */}
        <div className="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <img
              src={logo}
              alt="Techmentee Logo"
              className="w-64 mb-6 transition-transform duration-200 hover:scale-105"
            />
            <p className="text-gray-300 tracking-wide text-lg leading-relaxed mb-6">
              Techmentee is the premier training and placement agency in
              Atlanta, GA, USA, creating immersive digital learning experiences
              for learners globally.
            </p>

            {/* <div className="flex gap-4" role="list" aria-label="Social media links">
              {SOCIAL_LINKS.map((social, index) => (
                <SocialIcon key={index} {...social} />
              ))}
            </div> */}
          </div>

          {/* Quick Links */}
          <nav className="text-gray-300" aria-labelledby="quick-links-heading">
            <h3
              id="quick-links-heading"
              className="font-bold text-xl text-white border-b-2 border-primaryOrangeColor inline-block pb-2 mb-6"
            >
              Quick Links
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <NavigationLink to={link.to} className="text-lg">
                    {link.label}
                  </NavigationLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Programs */}
          <nav className="text-gray-300" aria-labelledby="programs-heading">
            <h3
              id="programs-heading"
              className="font-bold text-xl text-white border-b-2 border-primaryOrangeColor inline-block pb-2 mb-6"
            >
              Programs
            </h3>
            <div className="space-y-3">
              <NavigationLink
                to="/knowledge/program/6a27a298a4078e5f3d8e5fb2"
                className="block text-lg"
              >
                Career Enhancer
              </NavigationLink>
              <NavigationLink
                to="/knowledge/program/6835cdbcadc140f89bca5c32"
                className="block text-lg"
              >
                Career Launcher
              </NavigationLink>
              <NavigationLink
                to="/knowledge/program/career-explorer/clock-stopper"
                className="block text-lg"
              >
                Clock Stopper
              </NavigationLink>
              <NavigationLink
                to="/knowledge/program/career-explorer/hands-on-learn"
                className="block text-lg"
              >
                Hands-On Learn
              </NavigationLink>
              <NavigationLink
                to="/knowledge/program/career-explorer/employ-shield"
                className="block text-lg"
              >
                EmployShield
              </NavigationLink>

              {/*  {loading ? (
                <div className="flex items-center gap-2 text-gray-400">
                  <FaSpinner className="animate-spin" size={16} />
                  <span>Loading programs...</span>
                </div>
              ) : error ? (
                <div className="text-red-400 text-sm">
                  Unable to load programs
                </div>
              ) : (
                <ul className="space-y-3">
                  {programs.map((program) => (
                    <li key={program._id}>
                      <button
                        onClick={() => navigateToProgram(program)}
                        className="text-left hover:text-primaryOrangeColor transition-colors duration-200 text-lg focus:outline-none focus:text-primaryOrangeColor"
                        aria-label={`Navigate to ${program.title} program`}
                      >
                        {program.title}
                      </button>
                    </li> 
                  ))}
                </ul>
              )} */}
            </div>
          </nav>

          {/* Services */}
          <nav className="text-gray-300" aria-labelledby="services-heading">
            <h3
              id="services-heading"
              className="font-bold text-xl text-white border-b-2 border-primaryOrangeColor inline-block pb-2 mb-6"
            >
              Services
            </h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <NavigationLink to={service.to} className="text-lg">
                    {service.label}
                  </NavigationLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </footer>

      {/* Bottom Bar */}
      <div className="bg-[#7B96BD] text-white flex md:flex-row flex-col justify-between items-center lg:px-20 md:px-10 px-5 py-4 relative">
        <p className="text-sm md:text-base">
          Copyright © {new Date().getFullYear()} TECHMENTEE. All Rights Reserved
        </p>

        {/* Scroll to Top Button */}
        {isVisible && (
          <button
            onClick={scrollToTop}
            className="absolute md:left-[50%] md:right-auto right-0 -mt-12 bg-primaryOrangeColor p-2 rounded-full border border-gray-500 cursor-pointer"
            aria-label="Scroll to top of page"
          >
            <FaArrowUp size={16} className="text-white" />
          </button>
        )}
      </div>
    </>
  );
};

export default memo(Footer);
